import {
  type BoundaryGroup, type BoundaryGroupBase,
  type BoundaryGroupDetails,
} from '~/boundary/boundary.types';
import { createDemographicFromResponse } from '../demographics/demographics.factory';
import {
  type BoundaryGroupBaseResponse,
  type BoundaryGroupDetailsResponse, type BoundaryGroupMatchings, type BoundaryGroupMatchingsResponse,
  type BoundaryGroupResponse, type BoundaryGroupSettingsResponse,
} from './boundaryGroups.repository';

const createBoundaryGroupFromBase = (response: BoundaryGroupBaseResponse): BoundaryGroupBase => ({
  allowEditing: response.allow_editing,
  hideLabels: !(response.labels_enabled ?? true),
  clientId: response.client_id,
  id: response.boundary_group_id,
  loadZoomLevel: response.settings.load_zoom_level ?? 0,
  name: response.settings.display_name,
  sort: response.sort,
  userId: response.user_id,
  zoomLevels: response.settings.zoom_levels.sort((a, b) => a - b),
  matchings: createBoundaryGroupMatchingsFromResponse(response.matchings),
  allowOverlap: response.allow_overlap,
  readonly: response.readonly,
  wms: {
    territoriesCount: response.wms?.territories_count ?? 0,
    geometryRemainingCalls: response.wms?.geometry_remaining_calls ?? null,
    wmsTemporary: response.wms?.wms_temporary ?? false,
  },
  mot: {
    territoriesCount: response.mot?.territories_count ?? 0,
    motTemporary: response.mot?.mot_temporary ?? false,
    geometryRemainingCalls: response.mot?.geometry_remaining_calls ?? null,
  },
  createdFrom: createBoundaryGroupCreatedFromResponse(response.settings),
  editBoundaryZoomLevel: response.edit_boundary_zoom_level ?? undefined,
  visible: response.visible ?? true,
  graphsGenerated: response.graphs_generated ?? false,
  region: response.region,
  etag: response.etag ? {
    timestamp: response.etag.timestamp,
    lastUpdatedUserName: response.etag.last_updated_user_name,
    lastUpdatedUserId: response.etag.last_updated_user_id,
  } : undefined,
  provider: response.provider ?? undefined,
});

export const createBoundaryGroupFromResponse = (
  response: BoundaryGroupResponse
): BoundaryGroup => {
  const {
    demographics,
    codename,
    ...baseResponse
  } = response;

  return ({
    ...createBoundaryGroupFromBase(baseResponse),
    codename,
    demographics: demographics?.map(demographic => createDemographicFromResponse(demographic)) ?? [],
  });
};

export const createBoundaryGroupMatchingsFromResponse = (
  response: BoundaryGroupMatchingsResponse | Record<string, never>
): BoundaryGroupMatchings | null => {
  if (!response.type) {
    return null;
  }

  return {
    type: response.type,
    matchingColumns: response.matching_columns.map(column => ({
      id: column.id,
      name: column.name,
    })),
  };
};

export const createBoundaryGroupDetailsFromResponse = (
  response: BoundaryGroupDetailsResponse
): BoundaryGroupDetails => {
  const {
    used_on_maps,
    ...baseResponse
  } = response;

  return ({
    ...createBoundaryGroupFromBase(baseResponse),
    usedOnMaps: used_on_maps ?? [],
  });
};

export const createBoundaryGroupCreatedFromResponse = (responseSettings: BoundaryGroupSettingsResponse) => {
  const createdFrom = responseSettings.created_from;
  if (createdFrom) {
    return ({
      boundaryTerritoryGroupId: createdFrom.boundary_territory_group_id,
      boundaryGroupIds: createdFrom.boundary_group_ids,
    });
  }

  return undefined;
};
